import { Box } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { SteriliserRoutingRowType } from '../Scheduler.types'
import { SchedulerSteriliserRoutingTable } from '../SchedulerSteriliserRouting.muiTable'
import {
  useDeleteSchedulerSOR,
  useScheduleSORFull,
  useUpdateSchedulerSOR,
  useUploadScheduleDataFile,
} from './SOR.data'

// Steriliser routing tab. One row per (steriliserTypeId, basketFamilyId)
// with `lines` as a comma-separated string — mirrors the synchronized-lines
// convention so users get the same chip + multi-select editor for picking
// which lines route into a given steriliser/family pair.
export const SORSterilizerRouting = () => {
  const API = 'steriliser-routing'
  const { data: responseData, isLoading } =
    useScheduleSORFull<SteriliserRoutingRowType>(API)
  const serverData = responseData?.data ?? []
  const predicate = responseData?.predicate
  const availableLines = responseData?.availableLines ?? []

  const [localNewRows, setLocalNewRows] = useState<SteriliserRoutingRowType[]>(
    []
  )
  const [nextNewRowId, setNextNewRowId] = useState(-1)

  const data = useMemo(
    () => [...serverData, ...localNewRows],
    [serverData, localNewRows]
  )

  const onUpdateSuccess = () => {
    setLocalNewRows([])
    toast.success('Updated successfully.')
  }
  const onUpdateFailure = () => {
    toast.error('Failed to update.')
  }
  const { mutate: saveData, isLoading: isUpdating } =
    useUpdateSchedulerSOR<SteriliserRoutingRowType>(
      onUpdateSuccess,
      onUpdateFailure
    )

  const [allChangedRows, setAllChangedRows] = useState<
    Record<number, SteriliserRoutingRowType>
  >({})

  const updateTableData = (
    changedRows: Record<number, SteriliserRoutingRowType>
  ) => {
    setAllChangedRows({ ...allChangedRows, ...changedRows })
  }

  const handleAddRow = () => {
    const newRow: SteriliserRoutingRowType = {
      id: nextNewRowId,
      steriliserTypeId: '',
      basketFamilyId: `NEW_${Math.abs(nextNewRowId)}`,
      lines: '',
    }
    setLocalNewRows([...localNewRows, newRow])
    setNextNewRowId(nextNewRowId - 1)
  }

  useEffect(() => {
    if (Object.keys(allChangedRows).length > 0) {
      saveData({ data: allChangedRows, API })
      setAllChangedRows({})
    }
  }, [allChangedRows])

  const [updateRowsStatus, setUpdateRowsStatus] = useState({})

  const onUploadSuccess = (mergeInfo?: Record<string, unknown>) => {
    setUpdateRowsStatus(mergeInfo ?? {})
    toast.success('Data uploaded successfully')
  }
  const onUploadFailure = () => {
    toast.error('Failed to upload data')
  }
  const { mutate: uploadScheduleData, isLoading: isUploading } =
    useUploadScheduleDataFile(onUploadSuccess, onUploadFailure)

  const uploadFile = (file: File) => {
    uploadScheduleData({ file, API })
  }

  const onDeleteSuccess = () => {
    toast.success('Rows deleted successfully')
  }
  const onDeleteFailure = () => {
    toast.error('Failed to delete rows')
  }
  const { mutateAsync: deleteData, isLoading: isDeleting } =
    useDeleteSchedulerSOR(onDeleteSuccess, onDeleteFailure)

  const deleteTableData = async (
    rows: SteriliserRoutingRowType[]
  ): Promise<void> => {
    await deleteData({ rows, API })
  }

  return (
    <Box>
      <SchedulerSteriliserRoutingTable
        data={data}
        isLoading={isLoading}
        updateTableData={updateTableData}
        uploadScheduleData={uploadFile}
        isUploading={isUploading}
        isSaving={isUpdating}
        updateRowsStatus={updateRowsStatus}
        predicate={predicate as string[] | undefined}
        onAddRow={handleAddRow}
        availableLines={availableLines}
        onDeleteRows={deleteTableData}
        isDeleting={isDeleting}
      />
    </Box>
  )
}
